/**
 * Client-side image compression for gallery uploads.
 *
 * The gallery lets the user pick one of three quality presets:
 *   - "original" : send the file exactly as-is (default legacy behaviour)
 *   - "balanced" : max 3000px on the longest side, JPEG q=0.85. Looks great in
 *                  print and prints tiny WhatsApp thumbnails; ~1 MB per photo.
 *   - "fast"     : max 1800px on the longest side, JPEG q=0.72. Perfect for the
 *                  web-only guest gallery; ~250–400 KB per photo. Uploads screamers.
 *
 * We use a plain <canvas> so there is zero library cost. HEIC/HEIF files cannot
 * be decoded by the browser directly, so we pass them through untouched — the
 * backend will still ingest them (Pillow handles HEIC via pillow-heif).
 *
 * Falls back to the original file whenever:
 *   - preset === "original"
 *   - the file type is not a browser-decodable image
 *   - the compressed blob ends up larger than the source (rare, but happens
 *     for already-tiny web JPEGs) — we ship the smaller of the two.
 *   - anything throws — we never block an upload because of a compression bug.
 */

export const COMPRESSION_PRESETS = {
  original: { label: "Original", maxSide: Infinity, quality: 1.0, hint: "Full resolution — largest files, slowest upload" },
  balanced: { label: "Balanced", maxSide: 3000, quality: 0.85, hint: "Sharp on any screen or 8×10 print · ~1 MB / photo" },
  fast:     { label: "Fast",     maxSide: 1800, quality: 0.72, hint: "Web-optimised — fastest uploads · ~300 KB / photo" },
};

const BROWSER_DECODABLE = /^image\/(jpeg|jpg|png|webp)$/i;

async function fileToBitmap(file) {
  // createImageBitmap respects EXIF orientation on Chromium/Firefox/Safari.
  if (typeof createImageBitmap === "function") {
    try {
      return await createImageBitmap(file, { imageOrientation: "from-image" });
    } catch (err) {
      // Some Safari versions choke on the options bag — retry without.
      if (typeof console !== "undefined") {
        console.debug("createImageBitmap(options) failed, retrying:", err?.message);
      }
      try {
        return await createImageBitmap(file);
      } catch (err2) {
        // Fall through to the legacy <img> loader below.
        if (typeof console !== "undefined") {
          console.debug("createImageBitmap fallback failed:", err2?.message);
        }
      }
    }
  }
  // Legacy fallback via <img> + object URL.
  return await new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });
}

export async function compressImage(file, presetKey = "balanced") {
  const preset = COMPRESSION_PRESETS[presetKey] || COMPRESSION_PRESETS.balanced;
  if (presetKey === "original") return file;
  if (!file || !file.type) return file;
  if (!BROWSER_DECODABLE.test(file.type)) return file;                // HEIC etc.
  if (preset.maxSide === Infinity && preset.quality === 1.0) return file;

  try {
    const bitmap = await fileToBitmap(file);
    const w0 = bitmap.width || bitmap.naturalWidth;
    const h0 = bitmap.height || bitmap.naturalHeight;
    if (!w0 || !h0) return file;

    const scale = Math.min(1, preset.maxSide / Math.max(w0, h0));
    const w = Math.max(1, Math.round(w0 * scale));
    const h = Math.max(1, Math.round(h0 * scale));

    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d", { alpha: false });
    if (!ctx) return file;
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(bitmap, 0, 0, w, h);
    bitmap.close?.();

    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", preset.quality)
    );
    if (!blob || blob.size >= file.size) return file;

    const nameNoExt = (file.name || "photo").replace(/\.[^.]+$/, "");
    return new File([blob], `${nameNoExt}.jpg`, {
      type: "image/jpeg",
      lastModified: file.lastModified || Date.now(),
    });
  } catch (err) {
    // Compression should never block the upload — log for diagnostics and
    // fall back to the original file.
    if (typeof console !== "undefined") {
      console.warn("compressImage failed, sending original:", err?.message);
    }
    return file;
  }
}

export function humanBytes(n) {
  if (!Number.isFinite(n) || n <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}
